import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { STRIPE_PRICES } from '@/lib/stripe';

export interface StripePrice {
  id: string;
  unitAmount: number; // in cents
  currency: string;
  interval: 'month' | 'year' | null; // null for one-time
  intervalCount: number;
  type: 'recurring' | 'one_time';
}

export interface StripePricesData {
  monthly: StripePrice | null;
  sixMonth: StripePrice | null;
  yearly: StripePrice | null;
  // Formatted prices
  monthlyFormatted: string;
  sixMonthFormatted: string;
  sixMonthPerMonthFormatted: string;
  yearlyFormatted: string;
  yearlyPerMonthFormatted: string;
  // Trial info
  sixMonthTrialDays: number;
  yearlyTrialDays: number;
  // Savings calculations
  sixMonthSavingsPercent: number;
  yearlySavingsPercent: number;
}

// Format price in EUR
function formatEUR(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// Calculate savings percentage compared to monthly
function calculateSavings(monthlyAmount: number, totalAmount: number, months: number): number {
  const totalIfMonthly = monthlyAmount * months;
  if (totalIfMonthly <= 0) return 0;
  return Math.round(((totalIfMonthly - totalAmount) / totalIfMonthly) * 100);
}

export function useStripePrices() {
  return useQuery({
    queryKey: ['stripe-prices'],
    queryFn: async (): Promise<StripePricesData> => {
      try {
        // Call edge function to get prices from Stripe
        const { data, error } = await supabase.functions.invoke('stripe-get-prices', {
          body: {
            priceIds: [
              STRIPE_PRICES.PREMIUM_MONTHLY,
              STRIPE_PRICES.PREMIUM_6MONTH,
              STRIPE_PRICES.PREMIUM_1YEAR,
            ],
          },
        });

        if (error) {
          console.error('Error fetching Stripe prices:', error);
          throw error;
        }

        const prices = data?.prices as StripePrice[] | undefined;

        if (!prices || prices.length === 0) {
          throw new Error('No prices returned from Stripe');
        }

        // Find prices by ID
        const monthly = prices.find(p => p.id === STRIPE_PRICES.PREMIUM_MONTHLY) || null;
        const sixMonth = prices.find(p => p.id === STRIPE_PRICES.PREMIUM_6MONTH) || null;
        const yearly = prices.find(p => p.id === STRIPE_PRICES.PREMIUM_1YEAR) || null;

        // Get amounts (with fallbacks)
        const monthlyAmount = monthly?.unitAmount || 399; // 3,99€
        const sixMonthAmount = sixMonth?.unitAmount || 1499; // 14,99€
        const yearlyAmount = yearly?.unitAmount || 2399; // 23,99€

        const sixMonthPerMonth = Math.round(sixMonthAmount / 6);
        const yearlyPerMonth = Math.round(yearlyAmount / 12);

        return {
          monthly,
          sixMonth,
          yearly,
          monthlyFormatted: formatEUR(monthlyAmount),
          sixMonthFormatted: formatEUR(sixMonthAmount),
          sixMonthPerMonthFormatted: formatEUR(sixMonthPerMonth),
          yearlyFormatted: formatEUR(yearlyAmount),
          yearlyPerMonthFormatted: formatEUR(yearlyPerMonth),
          sixMonthTrialDays: 7,
          yearlyTrialDays: 7,
          sixMonthSavingsPercent: calculateSavings(monthlyAmount, sixMonthAmount, 6),
          yearlySavingsPercent: calculateSavings(monthlyAmount, yearlyAmount, 12),
        };
      } catch (err) {
        console.warn('Using fallback prices:', err);
        // Return fallback prices if API fails
        return {
          monthly: null,
          sixMonth: null,
          yearly: null,
          monthlyFormatted: '3,99 €',
          sixMonthFormatted: '14,99 €',
          sixMonthPerMonthFormatted: '2,50 €',
          yearlyFormatted: '23,99 €',
          yearlyPerMonthFormatted: '2,00 €',
          sixMonthTrialDays: 7,
          yearlyTrialDays: 7,
          sixMonthSavingsPercent: 37, // (3.99*6 - 14.99) / (3.99*6) ≈ 37%
          yearlySavingsPercent: 50, // (3.99*12 - 23.99) / (3.99*12) ≈ 50%
        };
      }
    },
    staleTime: 60 * 60 * 1000, // 1 hour - prices don't change often
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    retry: 2,
  });
}
